"use client";

// src/components/tooltip/index.tsx
import React2 from "react";
import { Tooltip as TT } from "radix-ui";
import { tooltip } from "@pinatagrams/design-system/recipes";
import { motion } from "motion/react";

// src/contexts/layer-context.tsx
import React from "react";

// src/contexts/layer-types.ts
var LayerType = /* @__PURE__ */ ((LayerType2) => {
  LayerType2[LayerType2["Main"] = 0] = "Main";
  LayerType2[LayerType2["Nav"] = 1] = "Nav";
  LayerType2[LayerType2["Popover"] = 2] = "Popover";
  LayerType2[LayerType2["Toasts"] = 3] = "Toasts";
  LayerType2[LayerType2["Overlay"] = 4] = "Overlay";
  return LayerType2;
})(LayerType || {});
var layerNameMap = {
  main: 0 /* Main */,
  nav: 1 /* Nav */,
  popover: 2 /* Popover */,
  toasts: 3 /* Toasts */,
  overlay: 4 /* Overlay */
};

// src/contexts/layer-context.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var LayerContext = React.createContext(null);
function Wrapper({
  children,
  layer
}) {
  const context = React.useContext(LayerContext);
  if (!context) {
    throw new Error("[Layer] Layer.Wrapper must be used within Layer");
  }
  const resolvedLayer = typeof layer === "string" ? layerNameMap[layer] : layer;
  const nextLayer = resolvedLayer >= context.layer ? resolvedLayer : context.layer;
  return /* @__PURE__ */ jsx(LayerContext.Provider, { value: { ...context, layer: nextLayer }, children });
}
function LayerRoot({ children }) {
  const baseLayerStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  };
  return /* @__PURE__ */ jsxs(LayerContext.Provider, { value: { layer: 0 /* Main */ }, children: [
    /* @__PURE__ */ jsx("style", { children: `
				[data-layer] > * {
					pointer-events: auto;
				}
			` }),
    /* @__PURE__ */ jsx("section", { id: "layer-popover", style: { ...baseLayerStyle, zIndex: 2 }, "data-layer": "popover" }),
    /* @__PURE__ */ jsx("section", { id: "layer-toasts", style: { ...baseLayerStyle, zIndex: 3 }, "data-layer": "toasts" }),
    /* @__PURE__ */ jsx(
      "section",
      {
        id: "layer-overlay",
        style: {
          ...baseLayerStyle,
          zIndex: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        },
        "data-layer": "overlay"
      }
    ),
    children
  ] });
}
function useLayerContainer(overrideLayer) {
  const context = React.useContext(LayerContext);
  const [container, setContainer] = React.useState(void 0);
  React.useLayoutEffect(() => {
    const defaultContainer = document.getElementById("layer-popover");
    if (!defaultContainer) {
      throw new Error("[Layer] Missing default popover layer container");
    }
    if (!context) return setContainer(defaultContainer);
    let targetLayer = context.layer;
    if (overrideLayer !== void 0) {
      targetLayer = overrideLayer >= context.layer ? overrideLayer : context.layer;
    }
    if (targetLayer === 0 /* Main */ || targetLayer === 1 /* Nav */) {
      setContainer(defaultContainer);
      return;
    }
    const layerId = LayerType[targetLayer].toLowerCase();
    const element = document.getElementById(`layer-${layerId}`);
    if (!element) {
      throw new Error(`[Layer] Missing container for layer: ${layerId}`);
    }
    setContainer(element);
  }, [context, overrideLayer]);
  return container;
}
var Layer = Object.assign(LayerRoot, { Wrapper });

// src/components/tooltip/index.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var TooltipProvider = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx2(TT.Provider, { ...props, children });
};
var TooltipRoot = ({
  children,
  open,
  onOpenChange,
  delayDuration = 0
}) => {
  const [isOpen, setIsOpen] = React2.useState(open ?? false);
  React2.useEffect(() => {
    if (open !== void 0) {
      setIsOpen(open);
    }
  }, [open]);
  const handleOpenChange = (newOpen) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  };
  return /* @__PURE__ */ jsx2(TT.Root, { delayDuration, open: isOpen, onOpenChange: handleOpenChange, children });
};
var TooltipTrigger = ({ children }) => {
  const triggerElement = React2.isValidElement(children) ? children : /* @__PURE__ */ jsx2("span", { children });
  return /* @__PURE__ */ jsx2(TT.Trigger, { asChild: true, children: triggerElement });
};
var TooltipContent = ({
  children,
  side = "top",
  sideOffset = 10,
  cardClassName,
  ...props
}) => {
  const container = useLayerContainer(2 /* Popover */);
  const MotionContent = React2.forwardRef((motionProps, ref) => /* @__PURE__ */ jsx2(motion.div, { ref, ...motionProps }));
  MotionContent.displayName = "MotionContent";
  return /* @__PURE__ */ jsx2(TT.Portal, { container, children: /* @__PURE__ */ jsx2(TT.Content, { side, sideOffset, ...props, children: /* @__PURE__ */ jsxs2(
    MotionContent,
    {
      className: tooltip().root,
      initial: { opacity: 0, y: 20, scale: 0.8 },
      animate: { opacity: 1, y: 0, scale: 1 },
      exit: {
        opacity: 0,
        y: 20,
        scale: 0.8,
        transition: { duration: 0.1 }
      },
      children: [
        /* @__PURE__ */ jsx2("div", { className: `${tooltip().card}${cardClassName ? ` ${cardClassName}` : ""}`, children }),
        /* @__PURE__ */ jsx2(TT.Arrow, { className: "tooltip-arrow" })
      ]
    }
  ) }) });
};
var Tooltip = Object.assign(TooltipRoot, {
  Trigger: TooltipTrigger,
  Content: TooltipContent,
  Provider: TooltipProvider
});
var tooltip_default = Tooltip;
export {
  Tooltip,
  TooltipProvider,
  tooltip_default as default
};
//# sourceMappingURL=tooltip.js.map